import React, { useMemo } from 'react';
import { Filter, Users, X, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../lib/api';
import type { ReportFormState } from './OutreachReportWizard';
import DistrictPicker from '../../../DistrictPicker';
import SegmentedControl from '../../../SegmentedControl';
import { buildDemographicQuestions } from '../../../../utils/chartableFields';

interface Props {
  itemId: string;
  formState: ReportFormState;
  onChange: (updates: Partial<ReportFormState>) => void;
}

interface QuestionFilter {
  question_id: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains';
  value: string;
}

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' }
];

/**
 * The three chambers a response can carry a district for, and the key prefix
 * each writes. The prefixes cannot collide: `'USHouse-2'.startsWith('House-')`
 * is false, because the dash is part of the comparison.
 */
/**
 * District fields the dedicated District card can already filter, so the
 * Question & Demographic list leaves them out. Derived from DISTRICT_CHAMBERS
 * below rather than restated, so adding a chamber picker removes it from the
 * generic list in the same edit.
 */
const DISTRICT_CHAMBERS = [
  { prefix: 'USHouse', chamber: 'US House', label: 'US House', hint: 'Select US House districts...', demoField: 'demo_district.us_house' },
  { prefix: 'Senate', chamber: 'Senate', label: 'State Senate', hint: 'Select State Senate districts...', demoField: 'demo_district.state_senate' },
  { prefix: 'House', chamber: 'House', label: 'State House', hint: 'Select State House districts...', demoField: 'demo_district.state_house' },
] as const;

const DISTRICT_FIELDS_WITH_A_PICKER = new Set<string>(
  DISTRICT_CHAMBERS.map(({ demoField }) => demoField)
);

const ReportStep2Filters: React.FC<Props> = ({ itemId, formState, onChange }) => {
  const filters = formState.filtersJson || {};

  // Fetch outreach item definition for questions
  const { data: itemData } = useQuery(
    ['outreach-item-def', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}`);
      return res.data.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch total response count
  const { data: responseData } = useQuery(
    ['outreach-response-count', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}/analytics-data?include=overview`);
      return res.data.data;
    },
    { staleTime: 60 * 1000 }
  );

  /*
    The three chambers the District card above already covers are dropped here:
    offering "State House District equals HD-12" in this list alongside a
    dedicated State House picker is the same filter twice, and the two could be
    set to contradict each other.

    County and Municipality are deliberately KEPT. They are district parts too,
    but the card has no picker for them, so this list is the only way to filter
    by them — removing all of demo_district.* would have quietly cost that.
  */
  const questions = useMemo(() => {
    const surveyQuestions = itemData?.definition_json?.questions || [];
    const demoQuestions = buildDemographicQuestions(itemData?.definition_json).filter(
      (q) => !DISTRICT_FIELDS_WITH_A_PICKER.has(q.id)
    );
    return [...surveyQuestions, ...demoQuestions];
  }, [itemData]);

  const totalResponses = responseData?.overview?.total_responses || 0;

  /*
    All three pickers write into the ONE `district` array. The keys already
    carry their chamber (`USHouse-2`, `House-12`, `Senate-5`), so splitting the
    control into three needed no storage change and no migration — a report
    saved before this still loads, and its selections land in the right boxes.
  */
  const selectedDistricts: string[] = Array.isArray(filters.district)
    ? filters.district
    : filters.district
      ? [filters.district]
      : [];

  const selectedForChamber = (prefix: string) =>
    selectedDistricts.filter((key) => key.startsWith(`${prefix}-`));

  const setForChamber = (prefix: string, next: string[]) => {
    const others = selectedDistricts.filter((key) => !key.startsWith(`${prefix}-`));
    const merged = [...others, ...next];
    updateFilters({ district: merged.length > 0 ? merged : undefined });
  };

  const selectedChamberCount = DISTRICT_CHAMBERS.filter(
    ({ prefix }) => selectedForChamber(prefix).length > 0
  ).length;

  const questionFilters: QuestionFilter[] = filters.question_filters || [];

  const updateFilters = (updates: Record<string, any>) => {
    onChange({ filtersJson: { ...filters, ...updates } });
  };

  const addQuestionFilter = () => {
    const newFilters = [...questionFilters, { question_id: '', operator: 'equals' as const, value: '' }];
    updateFilters({ question_filters: newFilters });
  };

  const updateQuestionFilter = (index: number, updates: Partial<QuestionFilter>) => {
    const newFilters = [...questionFilters];
    newFilters[index] = { ...newFilters[index], ...updates };
    updateFilters({ question_filters: newFilters });
  };

  const removeQuestionFilter = (index: number) => {
    const newFilters = questionFilters.filter((_, i) => i !== index);
    updateFilters({ question_filters: newFilters });
  };

  const hasDistrictFilter = Array.isArray(filters.district) ? filters.district.length > 0 : !!filters.district;
  const hasAnyFilters = !!(filters.date_from || filters.date_to || hasDistrictFilter || questionFilters.length > 0);

  // How many responses these filters actually match. Without this the step showed only
  // the total, so a filter matching nothing looked identical to one matching everything.
  const filtersKey = JSON.stringify(filters);
  const { data: filteredData, isFetching: matchLoading } = useQuery(
    ['outreach-response-count-filtered', itemId, filtersKey],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}/analytics-data`, {
        params: { include: 'overview', filters: filtersKey }
      });
      return res.data.data;
    },
    { staleTime: 60 * 1000, enabled: hasAnyFilters }
  );

  const matchedResponses: number | null = hasAnyFilters
    ? (filteredData?.overview?.total_responses ?? null)
    : totalResponses;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Data Filters
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
            Narrow down which responses are included in this report. Leave empty to include all responses.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Users className="w-4 h-4 text-text-tertiary" />
          <span className="text-text-primary dark:text-text-dark-primary font-medium">
            {!hasAnyFilters
              ? `${totalResponses} total response${totalResponses !== 1 ? 's' : ''}`
              : matchLoading || matchedResponses === null
                ? `Matching… of ${totalResponses}`
                : `${matchedResponses} of ${totalResponses} response${totalResponses !== 1 ? 's' : ''} match`}
          </span>
        </div>
      </div>

      {hasAnyFilters && !matchLoading && matchedResponses === 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
          <Filter className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            No responses match these filters. The report will be empty — loosen a filter, or
            clear them to include everything.
          </p>
        </div>
      )}

      {/* Date range */}
      <div className="card">
        <h4 className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-3">Date Range</h4>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs text-text-secondary dark:text-text-dark-secondary mb-1">From</label>
            <input
              type="date"
              value={filters.date_from ? filters.date_from.split('T')[0] : ''}
              onChange={(e) => updateFilters({ date_from: e.target.value ? `${e.target.value}T00:00:00Z` : undefined })}
              className="input w-full"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-text-secondary dark:text-text-dark-secondary mb-1">To</label>
            <input
              type="date"
              value={filters.date_to ? filters.date_to.split('T')[0] : ''}
              onChange={(e) => updateFilters({ date_to: e.target.value ? `${e.target.value}T23:59:59Z` : undefined })}
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {/* District — one picker per chamber, plus how they combine */}
      <div className="card">
        <h4 className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-3">District</h4>

        <div className="space-y-4">
          {DISTRICT_CHAMBERS.map(({ prefix, chamber, label, hint }) => (
            <div key={prefix}>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                {label}
              </label>
              <DistrictPicker
                selectedDistricts={selectedForChamber(prefix)}
                onChange={(districts) => setForChamber(prefix, districts)}
                chambers={[chamber]}
                /* Responses carry a congressional district from the address
                   lookup, so US House belongs here with the state chambers. */
                includeCongressional={chamber === 'US House'}
                placeholder={hint}
              />
            </div>
          ))}
        </div>

        {/*
          Only asked across chambers, never within one. A respondent has exactly
          one district per chamber, so several picks inside a single dropdown can
          only ever mean "any of these" — offering AND there would be a control
          whose second position always returns nothing.
        */}
        {selectedChamberCount > 1 && (
          <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
            <p className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Respondents must match
            </p>
            <SegmentedControl
              ariaLabel="How district filters combine"
              value={filters.district_match === 'all' ? 'all' : 'any'}
              onChange={(mode) => updateFilters({ district_match: mode })}
              options={[
                { id: 'any', label: 'Any of these districts' },
                { id: 'all', label: 'All of these districts' },
              ]}
            />
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
              {filters.district_match === 'all'
                ? 'A respondent must fall in a selected district from every chamber above.'
                : 'A respondent matching any one of the selected districts is included.'}
            </p>
          </div>
        )}

        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-3">
          Picking several districts within one chamber matches any of them. Search by district
          number or legislator name.
        </p>
      </div>

      {/* Question-specific filters */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Question &amp; Demographic Filters</h4>
          <button
            onClick={addQuestionFilter}
            className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Filter
          </button>
        </div>

        {questionFilters.length === 0 ? (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            No filters applied. Click &quot;Add Filter&quot; to filter by specific answers or demographics.
          </p>
        ) : (
          <div className="space-y-3">
            {questionFilters.map((qf, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-background-dark-secondary">
                <select
                  value={qf.question_id}
                  onChange={(e) => updateQuestionFilter(i, { question_id: e.target.value, value: '' })}
                  className="input flex-1 text-sm"
                >
                  <option value="">Select question...</option>
                  {questions.map((q: any) => (
                    <option key={q.id} value={q.id}>{q.question || q.label || q.id}</option>
                  ))}
                </select>
                <select
                  value={qf.operator}
                  onChange={(e) => updateQuestionFilter(i, { operator: e.target.value as QuestionFilter['operator'] })}
                  className="input w-40 text-sm"
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                {(() => {
                  const selectedQ = questions.find((q: any) => q.id === qf.question_id);
                  const opts = selectedQ?.options;
                  if (opts && opts.length > 0) {
                    return (
                      <select
                        value={qf.value}
                        onChange={(e) => updateQuestionFilter(i, { value: e.target.value })}
                        className="input flex-1 text-sm"
                      >
                        <option value="">Select value...</option>
                        {opts.map((o: any) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    );
                  }
                  return (
                    <input
                      type="text"
                      value={qf.value}
                      onChange={(e) => updateQuestionFilter(i, { value: e.target.value })}
                      placeholder="Value"
                      className="input flex-1 text-sm"
                    />
                  );
                })()}
                <button
                  onClick={() => removeQuestionFilter(i)}
                  className="p-1.5 text-red-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {hasAnyFilters && (
        <button
          onClick={() => onChange({ filtersJson: {} })}
          className="text-sm text-red-500 hover:text-red-600 transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
};

export default ReportStep2Filters;
